import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import PaginationPublic from "./shared/PaginationPublic";
import ProductSkeletonGrid from "./ProductSkeleton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBox } from "@fortawesome/free-solid-svg-icons";
import "../styles/products-list.css";

const ApiURL = process.env.REACT_APP_API_URL;

const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

const getTipoDotClass = (tipo) => {
  if (!tipo) return "default";
  const t = tipo.toLowerCase();
  if (t.includes("bater")) return "bateria";
  if (t.includes("explos") || t.includes("nafta") || t.includes("combusti")) return "explosion";
  if (t.includes("eléc") || t.includes("elec")) return "electrico";
  if (t.includes("combin")) return "combinado";
  if (t.includes("manual")) return "manual";
  return "default";
};

const ProductsList = ({
  categoryFilter, brandFilter, tipoFilter, searchTerm,
  page, onPageChange, sort, onSortChange,
  selCat, selectedSubObjects, onClearCat,
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1, totalPages: 1, totalProducts: 0,
    productsPerPage: 20, hasNextPage: false, hasPrevPage: false,
  });

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      let apiUrl = `${ApiURL}/products?paginate=true&page=${page}&limit=20`;
      if (categoryFilter) apiUrl += `&category=${categoryFilter}`;
      if (brandFilter)    apiUrl += `&brand=${brandFilter}`;
      if (tipoFilter)     apiUrl += `&tipo=${tipoFilter}`;
      if (searchTerm)     apiUrl += `&search=${searchTerm}`;
      if (sort)           apiUrl += `&sort=${sort}`;
      const response = await axios.get(apiUrl);
      setProducts(response.data.products);
      setPagination(response.data.pagination);
    } catch (error) {
      console.log("Error obteniendo los productos:", error);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, brandFilter, tipoFilter, searchTerm, page, sort]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  return (
    <div className="products-list-container">
      <p className="results-count">
        {loading
          ? "Cargando..."
          : (categoryFilter || brandFilter || tipoFilter || searchTerm)
            ? <>{pagination.totalProducts} producto{pagination.totalProducts !== 1 ? "s" : ""} encontrado{pagination.totalProducts !== 1 ? "s" : ""}</>
            : null
        }
      </p>

      {loading ? (
        <ProductSkeletonGrid count={21} />
      ) : products.length === 0 ? (
        <div className="no-products">
          <FontAwesomeIcon icon={faBox} className="no-products-icon" />
          <h3>No se encontraron productos</h3>
          <p>Intentá con otros filtros o términos de búsqueda</p>
        </div>
      ) : (
        <>
          <div className="products-grid">
            {products.map((product) => {
              const tags = product.tags || [];
              const tipo = product.tipo || null;
              return (
                <Link key={product.slug} to={`/productos/${product.slug}`} className="product-card-modern" onClick={scrollToTop}>
                  {product.featured && <span className="product-badge featured">Destacado</span>}
                  {!product.featured && product.isNew && <span className="product-badge new">Novedad</span>}

                  <div className="product-image-wrapper">
                    <img src={product.image} alt={product.name} className="product-image" loading="lazy" />
                  </div>

                  <div className="product-info">
                    {product.brand && <p className="product-brand-label">{product.brand.name}</p>}
                    <h3 className="product-name">{product.name}</h3>
                    {tags.length > 0 && (
                      <div className="product-tags">
                        {tags.slice(0, 3).map((tag, i) => (
                          <span key={i} className="product-tag">{tag}</span>
                        ))}
                      </div>
                    )}
                    <div className="product-card-footer">
                      {tipo ? (
                        <div className="product-tipo-row">
                          <span className={`product-tipo-dot ${getTipoDotClass(tipo)}`} />
                          <span>{tipo}</span>
                        </div>
                      ) : <span />}
                      <div className="product-card-cta">
                        <span className="product-view-button">Ver detalle</span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {pagination.totalPages > 1 && (
            <div className="products-pagination-wrapper">
              <PaginationPublic
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={(p) => { onPageChange(p); scrollToTop(); }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProductsList;